import {
  Box,
  BoxProps,
  Heading,
  Text,
  Dialog as ChakraDialog,
  DialogRootProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogBodyProps,
  DialogFooterProps,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import CrossIcon from 'src/assets/icons/CrossIcon';
import AttentionBox from 'src/pages/signup/AttentionBox';
import fonts from 'src/theme/fonts';
import colors from 'src/theme/colors';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  heading: string;
  Body: ReactNode;
  width?: DialogContentProps['width'];
  subheading?: string;
  Footer?: ReactNode;
  isBeta?: boolean;
  closeOnInteractOutside?: boolean;
  dialogContentProps?: BoxProps;
}

export default function Dialog({
  open,
  onClose,
  heading,
  subheading,
  Body: BodyComp,
  Footer: FooterComp,
  width,
  isBeta,
  closeOnInteractOutside = true,
  dialogContentProps,
  ...props
}: DialogProps & BoxProps) {
  return (
    <Container
      open={open}
      onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}
      closeOnInteractOutside={closeOnInteractOutside}
    >
      <Content width={width} maxW="1040px" {...dialogContentProps}>
        <Header heading={heading} subheading={subheading} isBeta={isBeta} />
        <Body {...props} pt="30">
          {BodyComp}
        </Body>
        {FooterComp && <Footer>{FooterComp}</Footer>}
      </Content>
    </Container>
  );
}

export function Container(props: DialogRootProps) {
  return (
    <ChakraDialog.Root trapFocus={false} placement="center" scrollBehavior="inside" {...props}>
      <ChakraDialog.Backdrop />
      <ChakraDialog.Positioner>{props.children}</ChakraDialog.Positioner>
    </ChakraDialog.Root>
  );
}

export function Content(props: DialogContentProps) {
  return <ChakraDialog.Content w={props.width || '600px'} maxH="95vh" maxW="95vw" {...props} />;
}

export function Header({
  heading,
  subheading,
  isBeta = false,
  ...props
}: DialogHeaderProps & { heading: string; subheading?: string; isBeta?: boolean }) {
  return (
    <ChakraDialog.Header
      borderBottom={`2px solid ${colors.podcastShade200}`}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      {...props}
    >
      <Box>
        <Heading as="h3" visual="dialogTitle">
          {heading}
          {isBeta && (
            <AttentionBox ml="7px" p="3px" verticalAlign="middle">
              BETA
            </AttentionBox>
          )}
        </Heading>
        {subheading && (
          <Text visual="lighter" fontFamily={fonts.normal} textAlign="start" mt="3px">
            {subheading}
          </Text>
        )}
      </Box>
      <ChakraDialog.CloseTrigger
        h="42px"
        w="42px"
        borderRadius="8px"
        position="static"
        cursor="pointer"
        aria-label="Dismiss"
      >
        <CrossIcon />
      </ChakraDialog.CloseTrigger>
    </ChakraDialog.Header>
  );
}

export function Body(props: DialogBodyProps) {
  return <ChakraDialog.Body px="40px" pb="30px" overflowY="auto" {...props} />;
}

export function Footer(props: DialogFooterProps) {
  return <ChakraDialog.Footer bg="podcastShade100" p="30px 40px" {...props} />;
}
